import React, { useState, useEffect } from 'react';
import '../../App.css';
import './user-control.css';
import '../../tools/search-results/search-results.css';
import CSS from 'csstype';
import '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

// functions

// types
import { FileBrowserPage, NotificationType, UserType } from '../../utils/types';

// images
import { Bell, FolderTree, Square, SquareCheckBig, Trash2 } from 'lucide-react';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import MiniButton from '../../tools/mini-button';
import {
  Event,
  EventType,
  EventTarget,
  InitiateDMSearchParams,
} from '../../utils/events';
import { handleEvent } from '../../app-actions';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

// class-level constants.
const TABLE_COL: CSS.Properties = {
  padding: '3px 5px 3px 5px',
  whiteSpace: 'normal',
  textAlign: 'left',
};
const TABLE_COL_CENTRED: CSS.Properties = {
  padding: '3px 5px 3px 5px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

//  --------------------------------------------------------------------------
//
//  H T M L   C U S T O M   C O M P O N E N T S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Constructs a list of columns.
//
//  ------------------------------------------------------------

function TableColumns() {
  return (
    <>
      <col style={{ width: '0px' }} key="delete" />
      <col style={{ width: '0px' }} key="unread" />
      <col style={{ width: '0px' }} key="selectCol" />
      <col key="message" />
      <col style={{ width: '0px' }} key="date" />
    </>
  );
} // TableColumns

//  ------------------------------------------------------------
//
//  Constructs a table header row from a list of column names.
//
//  ------------------------------------------------------------

function TableHeadings(props: {
  sSelectedRows: number[];
  setSelectedRows: any;
  sRenderCount: number;
  setRenderCount: any;
  sSelectAll: boolean;
  setSelectAll: any;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const sNotifications: NotificationType[] = useAppSelector(
    (state) => state.app.notifications,
  );

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  Button handler for check box click events.
  //
  //  ------------------------------------------------------------

  const checkChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    // invert the select all check box.
    const selectAll = !props.sSelectAll;
    props.setSelectAll(selectAll);

    // get the list of selected rows from state.
    var selectedRows: number[] = props.sSelectedRows;

    // loop over all the notifications, adding them to the selected-items list
    // if they're not already there.
    if (selectAll === true)
      for (var i: number = 0; i < sNotifications.length; i++)
        if (selectedRows.indexOf(sNotifications[i].notificationID) === -1)
          selectedRows.push(sNotifications[i].notificationID);

    // or clear the selected rows list.
    if (selectAll === false) selectedRows = [];

    // update state.
    props.setSelectedRows(selectedRows);
    props.setRenderCount(props.sRenderCount + 1);
    //
  }; // checkChange

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <>
      <th style={TABLE_COL} data-hidden="true" key="delete" />
      <th style={TABLE_COL} data-hidden="true" key="unread" />
      <th style={TABLE_COL_CENTRED} data-hidden="true" key="selectCol">
        <button
          name="chkNotificationCol"
          type="button"
          title="Select/deselect all"
          className="action-button text-colour"
          onClick={checkChange}
          style={{
            height: '22px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            background: 'transparent',
            border: 'none',
          }}
        >
          {props.sSelectAll === true ? (
            <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
          ) : (
            <Square width={25} height={25} strokeWidth={1.5} />
          )}
        </button>
      </th>
      <th style={TABLE_COL} data-hidden="true" key="message">
        Message
      </th>
      <th style={TABLE_COL} data-hidden="true" key="date">
        Date
      </th>
    </>
  );
  //
} // TableHeadings

//  ------------------------------------------------------------
//
//  A HTML component that takes a list of sources and renders
//  them as a list of table rows.
//
//  ------------------------------------------------------------

function TableRows(props: {
  sSelectedRows: number[];
  setSelectedRows: any;
  sRenderCount: number;
  setRenderCount: any;
  setSelectAll: any;
}) {
  //
  const dispatch = useAppDispatch();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  // global state variables.
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_userType: UserType =
    useAppSelector((state) => state.global.userType) ?? UserType.STANDARD;

  const sNotifications: NotificationType[] = useAppSelector(
    (state) => state.app.notifications,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  delete a single notification.
  //
  //  ------------------------------------------------------------

  const deleteNotificationHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pNotificationID: number,
  ) => {
    //
    // call the end point to remove the notification.
    const notificationIDs: number[] = [pNotificationID];

    // raise a delete-notification event.
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.DELETE_NOTIFICATIONS,
      parameters: notificationIDs,
    };
    dispatch(handleEvent({ event: newEvent }));
    //
  }; // deleteNotificationHandler

  //  ------------------------------------------------------------
  //
  //  format a date that is in text format.
  //
  //  ------------------------------------------------------------

  function formatDate(args: { createdAt: string }) {
    //
    const year: string = args.createdAt.slice(0, 4);
    const month: string = args.createdAt.slice(5, 7);
    const day: string = args.createdAt.slice(8, 10);
    const time: string = args.createdAt.slice(11, 16);
    const formattedDateTime: string = year + '-' + month + '-' + day + ' ' + time;

    // return something.
    return formattedDateTime;
    //
  } // formatDate

  //	------------------------------------------------------------
  //
  //	search the data-management API for the files being
  //	transferred by this job ID.
  //
  //	------------------------------------------------------------

  const initiateDMSearchHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
    pJobID: string,
  ) => {
    // run the data-management search.
    const params: InitiateDMSearchParams = {
      jobID: pJobID !== null ? pJobID : '',
      fileType: 'all',
      pageDisplayed: FileBrowserPage.JobDetails,
      showPage: true,
    };
    const newEvent: Event = {
      eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
      eventType: EventType.INITIATE_DM_SEARCH,
      parameters: params,
    };
    dispatch(handleEvent({ event: newEvent }));
  }; // initiateDMSearchHandler

  //  ------------------------------------------------------------
  //
  //  build the notification text depending upon the notificaiton
  //  type.
  //
  //  ------------------------------------------------------------

  function notificationText(args: { notification: NotificationType }) {
    //
    var textValue: string = args.notification.notificationText;

    // DMR: data-management transfer request (READY).
    if (args.notification.notificationType === 'DMR')
      textValue = textValue + ': ' + args.notification.additionalInfo.description;

    // return something.
    return textValue;
    //
  } // notificationText

  //  ------------------------------------------------------------
  //
  //  Button handler for check box onChange events.
  //
  //  ------------------------------------------------------------

  const rowCheckChange = (
    event: React.MouseEvent<HTMLButtonElement>,
    pNotificationID: number,
  ) => {
    //
    // get the list of selected rows from state.
    var selectedRows: number[] = props.sSelectedRows.slice();
    const pos: number = props.sSelectedRows.indexOf(pNotificationID);
    const ticked: boolean = pos === -1;

    // remove the select-all checkbox in the row header.
    if (ticked === false) props.setSelectAll(false);

    // delete the row.
    if (pos > -1 && ticked === false) selectedRows.splice(pos, 1);

    // add the row.
    if (pos === -1 && ticked === true) selectedRows.push(pNotificationID);

    // update state.
    props.setSelectedRows(selectedRows);
    props.setRenderCount(props.sRenderCount + 1);
    //
  }; // rowCheckChange

  return (
    <>
      {sNotifications.map((item, index) => (
        <tr key={index} className="search-results-table-row">
          <td style={TABLE_COL_CENTRED}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  flex: '0 0 2px',
                  height: '30px',
                  backgroundColor: item.readFlag ? 'transparent' : 'red',
                }}
              />
              <MiniButton
                onClick={(event) =>
                  deleteNotificationHandler(
                    /* event = */ event,
                    /* pNotificationID = */ item.notificationID,
                  )
                }
                title="Delete notification"
                style={{ marginLeft: '8px' }}
              >
                <Trash2 width={30} height={30} strokeWidth={1.5} />
              </MiniButton>
              {item.notificationType.toUpperCase() === 'DMR' &&
              [UserType.OPERATOR, UserType.DEVELOPER].includes(sG_userType) ? (
                <MiniButton
                  title="View data"
                  onClick={(event) =>
                    initiateDMSearchHandler(
                      /* event = */ event,
                      /* pJobID = */ item.additionalInfo.data_management_job_id,
                    )
                  }
                  style={{ marginLeft: '8px' }}
                >
                  <FolderTree width={30} height={30} strokeWidth={1.5} />
                </MiniButton>
              ) : (
                <></>
              )}
            </div>
          </td>
          <td style={{ ...TABLE_COL_CENTRED, ...{ width: 'auto' } }}>
            {item.readFlag !== true ? (
              <Bell className="text-colour" width={25} height={25} strokeWidth={1.5} />
            ) : (
              <></>
            )}
          </td>
          <td style={{ ...TABLE_COL_CENTRED, ...{ width: 'auto' } }}>
            <button
              type="button"
              title="Select/deselect row"
              className="action-button text-colour"
              onClick={(event) =>
                rowCheckChange(
                  /* event = */ event,
                  /* pNotificationID = */ item.notificationID,
                )
              }
              style={{
                height: '22px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
              }}
            >
              {props.sSelectedRows.includes(item.notificationID) ? (
                <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
              ) : (
                <Square width={25} height={25} strokeWidth={1.5} />
              )}
            </button>
          </td>
          <td style={{ ...TABLE_COL, ...{ fontWeight: item.readFlag ? '500' : '800' } }}>
            {notificationText({ notification: item })}
          </td>
          <td style={{ ...TABLE_COL, ...{ whiteSpace: 'nowrap' } }}>
            {formatDate({ createdAt: item.createdAt })}
          </td>
        </tr>
      ))}
    </>
  );
  //
} // TableRows

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function ViewNotifications() {
  //
  //  --------------------------------------------------------------------------
  //
  //  C O N S T A N T S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const sNotifications: NotificationType[] = useAppSelector(
    (state) => state.app.notifications,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // multi-language support
  const { i18n, t } = useTranslation();

  // which rows are currently selected?
  const [sSelectedRows, setSelectedRows] = useState<number[]>([]);
  const [sSelectAll, setSelectAll] = useState<boolean>(false);

  // update render count to refresh the notifications, and check the selected items.
  const [sRenderCount, setRenderCount] = useState<number>(0);

  const dispatch = useAppDispatch();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  delete one or more notifications.
  //
  //  ------------------------------------------------------------

  const deleteNotificationsHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    if (sSelectedRows.length > 0) {
      // raise a delete-notifications event.
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.DELETE_NOTIFICATIONS,
        parameters: sSelectedRows,
      };
      dispatch(handleEvent({ event: newEvent }));

      // ensure the select-all checkbox is unchecked.
      setSelectAll(false);

      // update state.
      setSelectedRows([]);
    }
    //
  }; // deleteNotificationsHandler

  //  ------------------------------------------------------------
  //
  //  Mark the listed notification IDs are READ in the database.
  //
  //  ------------------------------------------------------------

  function markNotificationsRead(args: { notificationIDs: number[] }) {
    //
    // call the end point to mark the notifications as read.
    const notificationIDs: number[] = args.notificationIDs;

    // raise a mark-notifications-read event.
    const newEvent: Event = {
      eventTarget: EventTarget.APP,
      eventType: EventType.MARK_NOTIFICATIONS_READ,
      parameters: notificationIDs,
    };
    dispatch(handleEvent({ event: newEvent }));
    //
  } // markNotificationsRead

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  set event handlers when this component loads, and set
  //  timeout that marks notifications are read.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    // mark the notifications as read after 5 seconds.
    setTimeout(() => {
      markNotificationsRead({
        notificationIDs: sNotifications.map(
          (notification) => notification.notificationID,
        ),
      });
    }, 3000);
    //
  }, []);

  //  ------------------------------------------------------------
  //
  //  define a hook that checks if any selected notification IDs no longer exist
  //  in the notifications state. if so, we need to remove them.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    // make a list of selected rows that still appear in the notifications list.
    const selectedRows: number[] = sSelectedRows.filter(
      (filter: number) =>
        sNotifications.findIndex(
          (find: NotificationType) => find.notificationID === filter,
        ) > -1,
    );

    // possibly set the select-all checkbox in the row header.
    if (sNotifications.length === selectedRows.length && selectedRows.length > 0) {
      setSelectAll(true);
    }

    // set state.
    if (selectedRows.length !== sSelectedRows.length) {
      setSelectedRows(selectedRows);
    }
    //
  }, [sRenderCount]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div
      role="region"
      aria-labelledby="view-notifications-label"
      className="gradient-background text-colour"
      style={{
        flex: '1 1',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: 'transparent',
        padding: '0px 10px 0px 0px',
      }}
    >
      <div
        className="panel-main"
        style={{
          flexDirection: 'column',
        }}
      >
        <div style={{ height: '0px', flex: '1 1 auto', width: '100%' }}>
          <div
            style={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <div style={{ flex: '1 1', display: 'flex', flexDirection: 'column' }}>
              <div
                id="view-notifications-label"
                className="header-text"
                style={{
                  flex: '0 0 45px',
                  alignItems: 'center',
                  display: 'flex',
                  width: '100%',
                }}
              >
                View notifications
              </div>

              <div style={{ flex: '1 1' }}>
                <div className="search-results-table-holder">
                  <table style={{ width: '99%' }}>
                    <colgroup>
                      <TableColumns />
                    </colgroup>
                    <thead>
                      <tr className="search-results-table-row-header">
                        <TableHeadings
                          sSelectedRows={sSelectedRows}
                          setSelectedRows={setSelectedRows}
                          sRenderCount={sRenderCount}
                          setRenderCount={setRenderCount}
                          sSelectAll={sSelectAll}
                          setSelectAll={setSelectAll}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      <TableRows
                        sSelectedRows={sSelectedRows}
                        setSelectedRows={setSelectedRows}
                        sRenderCount={sRenderCount}
                        setRenderCount={setRenderCount}
                        setSelectAll={setSelectAll}
                      />
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ flex: '0 0 10px' }} />
          </div>
        </div>
        <div style={{ flex: '0 0 5px' }} />

        <div
          key={sRenderCount}
          className="view-notification-footer br-d-t"
          style={{
            flex: '0 0 auto',
            alignItems: 'centre',
            display: sSelectedRows.length > 0 ? 'flex' : 'none',
            flexDirection: 'row',
            width: '100%',
            marginTop: '10px',
            paddingTop: '10px',
          }}
        >
          <div style={{ flex: '0 0 15px' }} />
          <div
            className="header-text"
            style={{ flex: '0 0 auto', alignItems: 'center', display: 'flex' }}
            data-bold="F"
          >
            {sSelectedRows.length.toString() + ' notification(s) selected:'}
          </div>
          <div style={{ flex: '0 0 50px' }} />
          <div style={{ flex: '0 0 auto' }}>
            <ToolButton
              key={t('Delete')}
              name="deleteNotifications"
              onClick={deleteNotificationsHandler}
              text={t('Delete')}
              type={ToolButtonType.SECONDARY}
            />
          </div>
          <div style={{ flex: '1 1' }} />
          <div style={{ flex: '0 0 15px' }} />
        </div>
      </div>
    </div>
  );
  //
} // ViewNotifications
